import type { AssignmentLine } from 'src/api/rpc/hooks/procurement/useReplenishmentSuggestions';

/** What a buyer can do to a line's approval state from its badge. */
export type ApprovalAction =
  'approved' | 'unapproved' | 'trouble' | 'rejected' | 'restore';

/**
 * The actions open to a line, in menu order, never including its current
 * state. A rejected line can only be restored (it comes back unapproved), and a
 * credit-held line cannot be approved: the hold keeps it off the order whatever
 * the buyer sets, so offering Approve would mislead. Unapprove stays, so a
 * buyer can take back a manual hold they put on top of the credit hold.
 */
export function approvalActions(line: AssignmentLine): ApprovalAction[] {
  // A released line is already on an order: nothing to change (GEN-3119).
  if (line.approvalState === 'released') return [];
  if (line.approvalState === 'rejected') return ['restore'];
  if (line.troubleReason === 'credit_hold') {
    return ['unapproved', 'trouble', 'rejected'];
  }
  const all: ApprovalAction[] = [
    'approved',
    'unapproved',
    'trouble',
    'rejected',
  ];
  // An auto-approved line is already approved; Approve would change nothing.
  const current =
    line.approvalState === 'auto_approved' ? 'approved' : line.approvalState;
  return all.filter((action) => action !== current);
}

/** A need row's actions when its SO lines are in different states. */
export const MIXED_APPROVAL_ACTIONS: readonly ApprovalAction[] = [
  'approved',
  'unapproved',
  'trouble',
  'rejected',
];

/** The action a single key press asks for on a focused badge. */
export function approvalActionForKey(
  key: string,
  line: AssignmentLine,
): ApprovalAction | null {
  switch (key.toLowerCase()) {
    case 'a':
      return 'approved';
    case 'u':
      return 'unapproved';
    case 't':
      return 'trouble';
    case 'r':
      return line.approvalState === 'rejected' ? 'restore' : 'rejected';
    default:
      return null;
  }
}
